import { NavLink } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  Boxes,
  ClipboardCheck,
  Github,
  LayoutDashboard,
  Rocket,
  ShieldAlert,
  Globe,
  X,
} from 'lucide-react';
import { cn } from '../../utils/cn';

const navItems = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/jira', label: 'Jira', icon: Activity },
  { to: '/security', label: 'Security', icon: ShieldAlert },
  { to: '/repositories', label: 'Repositories', icon: Github },
  { to: '/sites', label: 'Sites (WordPress)', icon: Globe },
  { to: '/infrastructure', label: 'Infrastructure', icon: Boxes },
  { to: '/deployments', label: 'Deployments', icon: Rocket },
  { to: '/playbooks', label: 'Playbooks', icon: BookOpen },
  { to: '/compliance', label: 'Compliance', icon: ClipboardCheck },
];

interface SidebarProps {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ mobileOpen, onCloseMobile }: SidebarProps) {
  return (
    <>
      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 -translate-x-full flex-col border-r border-slate-200 bg-white transition-transform duration-200 ease-out dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0',
          mobileOpen && 'translate-x-0',
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between px-5">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-pulse-600">
              <span className="absolute inline-flex h-2.5 w-2.5 animate-pulse-ring rounded-full bg-pulse-400" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-white" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">Pulse</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500">Health Academy</p>
            </div>
          </div>
          <button
            onClick={onCloseMobile}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2 scrollbar-thin">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onCloseMobile}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-pulse-50 text-pulse-700 dark:bg-pulse-900/30 dark:text-pulse-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
                )
              }
            >
              <Icon size={17} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-4 text-[11px] text-slate-400 dark:border-slate-800 dark:text-slate-600">
          Internal operational dashboard
          <br />
          Health Academy &copy; {new Date().getFullYear()}
        </div>
      </aside>
    </>
  );
}
